import subscriptionApi from 'api/subscription/subscription.api';
import {
  CONTACT_ACCOUNT_MANAGER_EMAIL,
  getSubscriptionMessage,
} from 'components/account-features/SubscriptionMessages';
import type {
  ModalMessageKeyTypes,
  SubscriptionMessageKeyTypes,
  TalkBeforeCancelForm,
} from 'redux/modules/subscriptions.types';
import {
  FEATURE_OR_LIMIT_NAME,
  MEMORY_LIMIT,
  OWNER_EMAIL,
  OWNER_NAME,
  SENDER_NAME,
  setSubscriptionMessageKey,
} from 'redux/modules/subscriptions.types';
import { showToast } from 'redux/modules/toasts.actions';
import {
  getAccountMemoryHistoryDays,
  getIsSubscriptionExpired,
  getIsTrialEnded,
} from 'redux/selectors/accounts';
import { getUpgradeContactInfo } from 'redux/selectors/subscriptions';
import format from 'string-template';
import type { Thunk } from 'types/redux';
import { isIntercomAvailable, showNewIntercomMessage } from 'utils/intercom-helper';
import { encodeAndComposeNewEmail } from 'utils/mail';

export function showAccountExpiredMessage(): Thunk {
  return (dispatch, getState) => {
    const state = getState();

    const isSubscriptionExpired = getIsSubscriptionExpired(state);
    const isTrialEnded = getIsTrialEnded(state);

    if (isTrialEnded) {
      dispatch(showSubscriptionNoticeModal('trialEnded'));
    } else if (isSubscriptionExpired) {
      dispatch(showSubscriptionNoticeModal('subscriptionExpired'));
    }
  };
}

/***
 * Set a `messageKey` to show a subscription-related modal
 * @param messageKey
 */
export const showSubscriptionNoticeModal =
  (messageKey: ModalMessageKeyTypes): Thunk =>
  dispatch => {
    dispatch(setSubscriptionMessageKey(messageKey));
  };

export const contactAccountManagerWithUpgradeRequest =
  (messageKey: SubscriptionMessageKeyTypes): Thunk =>
  (dispatch, getState) => {
    if (isIntercomAvailable()) {
      showNewIntercomMessage();
    } else {
      const info = getUpgradeContactInfo(getState());
      const memoryLimit = getAccountMemoryHistoryDays(getState());
      const message = getSubscriptionMessage(messageKey);

      if (!info) {
        dispatch(
          showToast({
            message: `Sorry, an error occurred while composing an e-mail to your account manager`,
            type: 'danger',
            title: 'Could not contact account manager',
          })
        );
        return;
      }

      encodeAndComposeNewEmail({
        to: CONTACT_ACCOUNT_MANAGER_EMAIL,
        subject: message.contactAccountManagerTemplate.subject,
        body: format(message.contactAccountManagerTemplate.body, {
          [FEATURE_OR_LIMIT_NAME]: message.featureOrLimitName,
          [SENDER_NAME]: info.userName,
          [OWNER_NAME]: info.ownerName,
          [OWNER_EMAIL]: info.ownerEmail,
          [MEMORY_LIMIT]: memoryLimit,
        }),
      });
    }
  };

export const contactWorkspaceAdminWithUpgradeRequest =
  (messageKey: SubscriptionMessageKeyTypes): Thunk =>
  (dispatch, getState) => {
    const info = getUpgradeContactInfo(getState());

    if (!info) {
      dispatch(
        showToast({
          message: `Sorry, an error occurred while composing an e-mail to your workspace admin`,
          type: 'danger',
          title: 'Could not contact workspace admin',
        })
      );
      return;
    }

    const memoryLimit = getAccountMemoryHistoryDays(getState());
    const message = getSubscriptionMessage(messageKey);

    encodeAndComposeNewEmail({
      to: info.ownerEmail,
      subject: message.contactWorkspaceAdminTemplate.subject,
      body: format(message.contactWorkspaceAdminTemplate.body, {
        [FEATURE_OR_LIMIT_NAME]: message.featureOrLimitName,
        [SENDER_NAME]: info.userName,
        [OWNER_NAME]: info.ownerName,
        [OWNER_EMAIL]: info.ownerEmail,
        [MEMORY_LIMIT]: memoryLimit,
      }),
    });
  };

// ? Move to subscription.cancel.actions?
export const submitTalkBeforeCancelForm =
  (payload: TalkBeforeCancelForm): Thunk<Promise<{ ok: boolean }>> =>
  () => {
    return subscriptionApi.saveTalkBeforeCancelForm(payload).catch(error => {
      throw new Error(error);
    });
  };

export const cancelSubscriptionAndRedirect = (): Thunk<Promise<unknown>> => () => {
  return subscriptionApi.cancelSubscriptionAndRedirect().then(() => {
    // Hard reload to re-initialize app without an active subscription
    window.location.href = `/settings/subscription`;
  });
};
